import { IOrbProps } from "./App";
import { useApi } from './components/useApi';
import { useUserContext } from './components/useUserContext';
import { useDataContext } from './components/useDataContext';
import { useIntegration } from './components/useIntegration';
import React, { useState } from "react";
import { HashRouter, Switch, Route } from 'react-router-dom';
import { ErrorBoundary } from "./components/ErrorBoundary";
import { IntegrationContext } from "./components/IntegrationContext";
import { DataContext } from "./components/DataContext";
import { OrbUserContext } from "./components/OrbUserContext";
import { CrLoadingOverlay } from './components/cr/CrLoadingOverlay';
import { AppGlobals, AppIDs } from "./AppGlobals";
import { withErrorHandling } from './components/withErrorHandling';
import { CloeAIMenu } from "./components/CloeAIMenu";
import { NotAuthorised, NotFound } from "./Routes";
import LoadingWhite from "./LoadingWhite";
import { useContentStyle } from "./components/ContentStyleContext";
import { TopBanner } from "./TopBanner";
import { MenuToggle } from "./components/MenuToggle";
import { useHistory, useLocation } from "react-router-dom";
import { buildCloeAIBreadcrumb } from "./breadcrumbs/cloeAIBreadcrumb";
import { CloeAIWelcome } from "./components/AI/CloeAIWelcome";
import App04CloeAIAdmin from "./components/App04CloeAIAdmin";
import AiChat from "./components/AI/AiChat";
import { IEntityDescription1 } from "./types";

const CloeAIApp: React.FunctionComponent<IOrbProps> = (props: IOrbProps) => {

    const history = useHistory();
    const location = useLocation();

    const [lastTopicIdForChilds, setLastTopicIdForChilds] = useState(null);
    const [entityDescriptionForMenu, setEntityDescriptionForMenu] = useState<IEntityDescription1>();

    const { dataServices, errorHandling } = props;

    const apiConnected = useApi(dataServices);
    const uc = useUserContext(apiConnected, dataServices, errorHandling);
    const ld = useDataContext(apiConnected, dataServices, errorHandling);

    const integration = useIntegration({
        disableDirectorateManagement: false,
        disableGroupManagement: false,
        disablePartnerOrganisationManagement: false,
        disableProjectManagement: false,
        disableUserManagement: false,
        dataSourceName: ''
    });

    const routeProps = {
        ...props,
        menuData: props.menuData,
        apiConnected: apiConnected,
        ...uc,
        ...ld
    };

    const commonProps = {
        lastTopicIdForChilds: lastTopicIdForChilds,
        setLastTopicIdForChilds: setLastTopicIdForChilds,
        entityDescriptionForMenu: entityDescriptionForMenu,
        onEntityDescriptionChange: setEntityDescriptionForMenu,
        ...routeProps,
    };

    const [isNavigationVisible, setNavigationVisible] = useState(false);
    const toggleNavigation = () => setNavigationVisible(!isNavigationVisible);

    const { contentStyle } = useContentStyle();

    if (uc.userContext.UserId === null) return (<LoadingWhite />);

    const appName = props.menuData.filter(m => m.ID === AppIDs.CLOEAI)[0].AppName;

    const breadcrumbItems = buildCloeAIBreadcrumb({
        customerTitle: AppGlobals.CustomerTitle,
        appName,
        location,
        history,
        lastTopicIdForChilds
    });

    return (
        <React.Fragment>
            <ErrorBoundary>
                <IntegrationContext.Provider value={integration}>
                    <DataContext.Provider value={ld}>
                        <OrbUserContext.Provider value={uc}>

                            <div className="main">
                                <div className="container">

                                    {/* LEFT NAVIGATION (drawer) */}
                                    <div className={`navigation ${isNavigationVisible ? 'show' : ''}`}>
                                        {!uc.userContext.UserId
                                            ? <CrLoadingOverlay isLoading={true} />
                                            : <CloeAIMenu {...commonProps} onLinkClicked={toggleNavigation} />
                                        }
                                    </div>

                                    {/* BACKDROP */}
                                    <div
                                        className={`nav-backdrop ${isNavigationVisible ? 'show' : ''}`}
                                        onClick={toggleNavigation}
                                        aria-hidden={!isNavigationVisible}
                                    />

                                    {/* RIGHT CONTENT AREA */}
                                    <div className="right-area">
                                        <div className="top-banner">
                                            <MenuToggle onToggleNavigation={toggleNavigation} />

                                            <TopBanner
                                                customerTitle={AppGlobals.CustomerTitle}
                                                userName={AppGlobals.UserTitle}
                                                breadcrumbItems={breadcrumbItems}
                                                onLogout={props.onLogout}
                                            />
                                        </div>

                                        <div className="content" style={contentStyle}>
                                            <HashRouter>
                                                <Switch>

                                                    <Route exact path="/a14/welcome" {...routeProps}>
                                                        <CloeAIWelcome />
                                                    </Route>

                                                    <Route exact path="/a14/topics" {...routeProps}>
                                                        <App04CloeAIAdmin {...commonProps} entity="Topics" />
                                                    </Route>

                                                    <Route exact path="/a14/chats" {...routeProps}>
                                                        <App04CloeAIAdmin {...commonProps} entity="AIChats" />
                                                    </Route>

                                                    <Route path="/a14/chats-for-topic/:topicIdForChats" {...routeProps}>
                                                        <App04CloeAIAdmin {...commonProps} entity="AIChats" />
                                                    </Route>

                                                    <Route exact path="/a14/ask-cloe/:chatId" {...routeProps}>
                                                        <AiChat {...commonProps} />
                                                    </Route>

                                                    <Route exact path="/a14/settings/item-1" {...routeProps}>
                                                        <div>Settings - Item 1<div /></div>
                                                    </Route>

                                                    <Route path="/not-authorised">
                                                        <NotAuthorised />
                                                    </Route>

                                                    <Route path="*">
                                                        <NotFound />
                                                    </Route>

                                                </Switch>
                                            </HashRouter>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </OrbUserContext.Provider>
                    </DataContext.Provider>
                </IntegrationContext.Provider>
            </ErrorBoundary>
        </React.Fragment>
    );
};

export const CloeAIAppWithErrorHandling =
    withErrorHandling(CloeAIApp);